import type {
  BedDto,
  BedSummaryDto,
  CreateBedInput,
  CreateHouseInput,
  CreateRoomInput,
  RoomSummaryDto,
  UpdateBedInput,
  UpdateHouseInput,
  UpdateRoomInput,
} from '@room-mates/shared';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { HouseWithRoomsDto } from '../../shared/api/api.js';
import { queryKeys } from '../../shared/api/queryKeys.js';
import { useApi } from '../../shared/auth/useAuth.js';

/**
 * Housing writes touch the aggregate, not one row: adding a bed changes the
 * room's bed count and the house's totals. Every mutation therefore refreshes
 * the whole house branch of the cache rather than trying to patch it by hand.
 */
function useHouseInvalidation(): (houseId?: string) => Promise<void> {
  const queryClient = useQueryClient();
  return async (houseId?: string) => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.houses.all });
    await queryClient.invalidateQueries({ queryKey: queryKeys.rooms.all });
    if (houseId !== undefined) {
      await queryClient.invalidateQueries({ queryKey: queryKeys.houses.detail(houseId) });
    }
  };
}

export function useHouses() {
  const api = useApi();
  return useQuery({ queryKey: queryKeys.houses.all, queryFn: () => api.houses.list() });
}

export function useHouse(houseId: string): UseQueryResult<HouseWithRoomsDto> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.houses.detail(houseId),
    queryFn: () => api.houses.get(houseId),
  });
}

export function useBeds(houseId: string, roomId: string): UseQueryResult<BedDto[]> {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.houses.beds(houseId, roomId),
    queryFn: () => api.beds.list(houseId, roomId),
  });
}

export function useCreateHouse() {
  const api = useApi();
  const invalidate = useHouseInvalidation();
  return useMutation({
    mutationFn: (input: CreateHouseInput) => api.houses.create(input),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateHouse(houseId: string) {
  const api = useApi();
  const invalidate = useHouseInvalidation();
  return useMutation({
    mutationFn: (input: UpdateHouseInput) => api.houses.update(houseId, input),
    onSuccess: () => invalidate(houseId),
  });
}

export function useDeleteHouse() {
  const api = useApi();
  const invalidate = useHouseInvalidation();
  return useMutation({
    mutationFn: (houseId: string) => api.houses.remove(houseId),
    onSuccess: () => invalidate(),
  });
}

export function useCreateRoom(houseId: string) {
  const api = useApi();
  const invalidate = useHouseInvalidation();
  return useMutation({
    mutationFn: (input: CreateRoomInput) => api.rooms.create(houseId, input),
    onSuccess: () => invalidate(houseId),
  });
}

export function useUpdateRoom(houseId: string) {
  const api = useApi();
  const invalidate = useHouseInvalidation();
  return useMutation({
    mutationFn: ({ roomId, input }: { roomId: string; input: UpdateRoomInput }) =>
      api.rooms.update(houseId, roomId, input),
    onSuccess: () => invalidate(houseId),
  });
}

export function useDeleteRoom(houseId: string) {
  const api = useApi();
  const invalidate = useHouseInvalidation();
  return useMutation({
    mutationFn: (roomId: string) => api.rooms.remove(houseId, roomId),
    onSuccess: () => invalidate(houseId),
  });
}

export function useCreateBed(houseId: string, roomId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const invalidate = useHouseInvalidation();
  return useMutation({
    mutationFn: (input: CreateBedInput) => api.beds.create(houseId, roomId, input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.houses.beds(houseId, roomId) });
      await invalidate(houseId);
    },
  });
}

export function useUpdateBed(houseId: string, roomId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const invalidate = useHouseInvalidation();
  return useMutation({
    mutationFn: ({ bedId, input }: { bedId: string; input: UpdateBedInput }) =>
      api.beds.update(houseId, roomId, bedId, input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.houses.beds(houseId, roomId) });
      await invalidate(houseId);
    },
  });
}

export function useDeleteBed(houseId: string, roomId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const invalidate = useHouseInvalidation();
  return useMutation({
    mutationFn: (bedId: string) => api.beds.remove(houseId, roomId, bedId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.houses.beds(houseId, roomId) });
      await invalidate(houseId);
    },
  });
}

/**
 * Every room the user owns, for pickers and for the rooms page.
 *
 * This used to fan out over the houses and stitch their details together, which
 * cost one request per house to render one flat list; the API now answers it in
 * one call.
 */
export function useAllRooms(): UseQueryResult<RoomSummaryDto[]> {
  const api = useApi();
  return useQuery({ queryKey: queryKeys.rooms.all, queryFn: () => api.rooms.listAll() });
}

/** Every bed the user owns, across all houses and rooms — the overall calendar's inventory. */
export function useAllBeds(): UseQueryResult<BedSummaryDto[]> {
  const api = useApi();
  return useQuery({ queryKey: queryKeys.beds.all, queryFn: () => api.beds.listAll() });
}
